import { NavLink } from "react-router-dom";
import { APP_NAME, getAppBranding, getCurrentTenant, getTenantMarkSrc } from "@/lib/appMeta";
import { TENANT_BRANDING } from "@/lib/tenantBranding";
import { useTheme } from "@/hooks/use-theme";
import { cn } from "@/lib/utils";

export function BrandLogo({ showText = true, className = "" }) {
  const branding = getAppBranding();
  const tenantBranding = TENANT_BRANDING[getCurrentTenant()];
  const { isDark } = useTheme();
  const label = tenantBranding ? `${tenantBranding.label} ${APP_NAME}` : APP_NAME;

  // A tenant's own mark leads when one exists — the Invoice Manager icon
  // steps aside and the product name rides underneath as the subtitle.
  const markSrc = getTenantMarkSrc(tenantBranding, branding.logo);
  const title = tenantBranding?.label ?? APP_NAME;
  const subtitle = tenantBranding ? APP_NAME : null;

  // Two lines of text sit taller than the single wordmark the mark was sized
  // for, so it grows to span both rather than floating small beside them.
  const hasSubtitle = showText && Boolean(subtitle);
  const markSize = hasSubtitle ? 36 : 32;

  const wordmarkSrc =
    isDark && tenantBranding?.wordmarkDark ? tenantBranding.wordmarkDark : tenantBranding?.wordmark;

  return (
    <NavLink
      to="/my-invoices"
      title={label}
      aria-label={label}
      className={cn("brand-logo flex shrink-0 items-center gap-2", className)}
    >
      {/* 32px, per the design system's brand-mark spec. It was 36px, which is
          also what pushed the name into an ellipsis once the sidebar narrowed. */}
      <span
        className="brand-logo-frame"
        style={{ width: markSize, height: markSize }}
        aria-hidden="true"
      >
        <img
          src={markSrc}
          alt=""
          width={markSize}
          height={markSize}
          className="brand-logo-image shrink-0 object-contain"
          style={{ width: markSize, height: markSize }}
        />
      </span>
      {/* The product name is never abbreviated: "Invoice Manage…" is not a
          shorter brand, it is a broken one. It holds its full width and the
          row's flexible space comes from elsewhere. */}
      {showText && (
        <span className="flex min-w-0 flex-col justify-center">
          {wordmarkSrc ? (
            <img src={wordmarkSrc} alt={title} className="brand-logo-wordmark shrink-0" />
          ) : (
            <p className="shrink-0 whitespace-nowrap text-[15px] font-semibold leading-tight tracking-tight text-foreground">
              {title}
            </p>
          )}
          {subtitle && (
            <span className="brand-logo-subtitle shrink-0 whitespace-nowrap">{subtitle}</span>
          )}
        </span>
      )}
    </NavLink>
  );
}
